import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Container, ArrowRightLeft, DollarSign, Droplets, TrendingUp, TrendingDown,
  BarChart3, PieChart as PieIcon, Activity, ChevronLeft, ChevronRight, Calendar,
  AlertTriangle, ChevronDown, ChevronUp, Wrench,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  Line, PieChart, Pie, Cell, ComposedChart, LabelList,
} from "recharts";
import { PageHeader } from "../components/Layout";
import { api } from "../lib/api";

// 8-hue palette on-brand (derived from Portside core but expanded)
const CARRIER_PALETTE = [
  "#006E9E", "#99D420", "#F1471D", "#008DC6",
  "#7C3AED", "#E11D48", "#0EA5E9", "#A16207",
  "#15803D", "#1E293B", "#DB2777",
];

const fmtMoney = (n, ccy = "USD") =>
  `${ccy} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString();
const fmtShortMoney = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
};

// ---------- Sub: KPI Cards ----------
function KpiCard({ label, value, sub, icon: Icon, accent, delta, deltaInverse, testid }) {
  const isUp = delta != null && (deltaInverse ? delta < 0 : delta > 0);
  const isDown = delta != null && (deltaInverse ? delta > 0 : delta < 0);
  const DeltaIcon = isUp ? TrendingUp : TrendingDown;
  const deltaColor = isUp ? "text-[#99D420]" : isDown ? "text-[#F1471D]" : "text-gray-400";
  return (
    <div className="border border-gray-200 bg-white p-5 relative overflow-hidden" data-testid={testid}>
      <div className={`absolute right-0 top-0 w-24 h-24 -mr-8 -mt-8 rounded-full opacity-10 ${accent}`} />
      <div className="relative">
        <div className="flex items-start justify-between mb-3">
          <div className={`w-10 h-10 flex items-center justify-center rounded-sm ${accent}`}>
            <Icon className="w-5 h-5" />
          </div>
          {delta != null && (
            <div className={`inline-flex items-center gap-1 text-xs font-bold ${deltaColor}`}>
              <DeltaIcon className="w-3.5 h-3.5" />
              {Math.abs(delta).toFixed(1)}%
            </div>
          )}
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{label}</div>
        <div className="font-mono font-black text-3xl text-gray-900 mt-1 tabular-nums">{value}</div>
        {sub && <div className="text-xs text-gray-500 mt-1.5">{sub}</div>}
      </div>
    </div>
  );
}

// ---------- Sub: Daily Heatmap (GitHub-style) ----------
function DailyHeatmap({ rows }) {
  if (!rows?.length) return null;
  const max = Math.max(...rows.map((r) => r.total), 1);
  const colorFor = (v) => {
    if (v === 0) return "#F3F0E0";
    const pct = v / max;
    if (pct < 0.2) return "#D7E9C6";
    if (pct < 0.4) return "#B8DC8B";
    if (pct < 0.6) return "#99D420";
    if (pct < 0.8) return "#006E9E";
    return "#003147";
  };
  // Group into weeks (cols) × days (rows), starting from oldest Monday
  const first = new Date(rows[0].day);
  const firstDow = first.getDay(); // 0=Sun
  const startOffset = firstDow === 0 ? 6 : firstDow - 1; // align to Monday=0
  const cells = Array(startOffset).fill(null).concat(rows);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return (
    <div className="overflow-x-auto pb-2" data-testid="dashboard-heatmap">
      <div className="flex gap-[3px] min-w-fit">
        {weeks.map((wk, wi) => (
          <div key={wi} className="flex flex-col gap-[3px]">
            {[0, 1, 2, 3, 4, 5, 6].map((dow) => {
              const cell = wk[dow];
              if (!cell) return <div key={dow} className="w-[14px] h-[14px]" />;
              return (
                <div
                  key={dow}
                  title={`${cell.day} — ${cell.IN} IN / ${cell.OUT} OUT (${cell.total} total)`}
                  className="w-[14px] h-[14px] rounded-sm"
                  style={{ backgroundColor: colorFor(cell.total) }}
                />
              );
            })}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-4 text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500">
        Less
        {["#F3F0E0", "#D7E9C6", "#B8DC8B", "#99D420", "#006E9E", "#003147"].map((c) => (
          <div key={c} className="w-3 h-3 rounded-sm" style={{ backgroundColor: c }} />
        ))}
        More
      </div>
    </div>
  );
}

// ---------- Sub: Tooltip ----------
function ChartTooltip({ active, payload, label, valueFormatter = (v) => v, hideZeros = false }) {
  if (!active || !payload?.length) return null;
  // For the revenue stacked-bar tooltip we hide $0 rows — historic carriers
  // that had activity in the past 6 months but not in the hovered month were
  // fragmenting the tooltip with `MAXAM: 0`, `CTH: 0`, `GREENPORT: 0`, etc.
  // The hidden zero rows are still on the bar itself (as invisible slices),
  // just kept out of the hover popover for readability.
  const rows = hideZeros ? payload.filter((p) => Number(p.value || 0) !== 0) : payload;
  if (!rows.length) return null;
  return (
    <div className="bg-white border border-gray-200 shadow-lg p-3 text-xs font-mono">
      <div className="font-bold text-gray-900 mb-1.5">{label}</div>
      {rows.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-gray-600 mr-2">{p.dataKey}:</span>
          <span className="font-bold text-gray-900 ml-auto">{valueFormatter(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ---------- Sub: Section Card ----------
function SectionCard({ title, subtitle, icon: Icon, action, children, testid }) {
  return (
    <div className="border border-gray-200 bg-white" data-testid={testid}>
      <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#006E9E]" />}
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-700">{title}</div>
            {subtitle && <div className="text-[10px] text-gray-500 mt-0.5">{subtitle}</div>}
          </div>
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// ---------- Sub: Operations Audit (gate-history gaps) ----------
function OperationsAuditCard() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = (d = days) => {
    setLoading(true);
    api.get(`/audit/operations-gaps?days=${d}`)
      .then((r) => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  const total = data?.total_issues ?? 0;
  const tone = total === 0
    ? "bg-emerald-50 border-emerald-300 text-emerald-900"
    : total <= 5
      ? "bg-amber-50 border-amber-400 text-amber-900"
      : "bg-red-50 border-red-400 text-red-900";

  return (
    <div className={`border-2 ${tone}`} data-testid="ops-audit-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3 flex items-center justify-between text-left"
        data-testid="ops-audit-toggle"
      >
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold">
              Operations audit — gate history gaps
            </div>
            <div className="text-[10px] opacity-80 mt-0.5">
              {loading ? "Scanning…" : total === 0
                ? (() => {
                    const rOut = data?.resolved_hidden?.outs || 0;
                    const rWash = data?.resolved_hidden?.washes || 0;
                    const r = rOut + rWash;
                    return r > 0
                      ? `No open gaps in the last ${data?.window_days || days} days. (${r} earlier gap${r === 1 ? "" : "s"} since closed by a later IN.)`
                      : `No gaps in the last ${data?.window_days || days} days. Gate-in compliance is clean.`;
                  })()
                : (() => {
                    const rOut = data?.resolved_hidden?.outs || 0;
                    const rWash = data?.resolved_hidden?.washes || 0;
                    const r = rOut + rWash;
                    const base = `${total} issue${total === 1 ? "" : "s"} in the last ${data?.window_days || days} days — ${data?.orphan_outs?.count || 0} OUT${(data?.orphan_outs?.count || 0) === 1 ? "" : "s"} without IN · ${data?.orphan_washes?.count || 0} wash${(data?.orphan_washes?.count || 0) === 1 ? "" : "es"} without IN`;
                    return r > 0 ? `${base} (+${r} since closed)` : base;
                  })()}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => { e.stopPropagation(); setDays(Number(e.target.value)); }}
            onClick={(e) => e.stopPropagation()}
            className="ps-input h-7 text-[11px] font-mono"
            data-testid="ops-audit-window"
          >
            <option value={7}>7d</option>
            <option value={14}>14d</option>
            <option value={30}>30d</option>
            <option value={60}>60d</option>
            <option value={90}>90d</option>
          </select>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>
      {open && data && (
        <div className="border-t border-current/20 p-5 space-y-4 text-sm bg-white text-gray-900">
          {(data.orphan_outs?.count || 0) > 0 && (
            <div data-testid="ops-audit-orphan-outs">
              <div className="text-[11px] uppercase tracking-wider font-bold text-red-700 mb-2">
                OUT events without a preceding IN ({data.orphan_outs.count})
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-1 px-2">Container</th>
                      <th className="py-1 px-2">Carrier</th>
                      <th className="py-1 px-2">Customer</th>
                      <th className="py-1 px-2">Yard</th>
                      <th className="py-1 px-2">OUT date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.orphan_outs.items.slice(0, 20).map((o) => (
                      <tr key={o.id} className="border-b border-gray-100">
                        <td className="py-1 px-2 font-mono font-bold">{o.container_number}</td>
                        <td className="py-1 px-2">{o.carrier_code || "—"}</td>
                        <td className="py-1 px-2 text-gray-600">{(o.customer || "—").slice(0, 28)}</td>
                        <td className="py-1 px-2">{o.yard_code || "—"}</td>
                        <td className="py-1 px-2 font-mono">{(o.event_time || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.orphan_outs.items.length > 20 && (
                  <div className="text-[11px] text-gray-500 mt-2">… and {data.orphan_outs.items.length - 20} more.</div>
                )}
              </div>
            </div>
          )}
          {(data.orphan_washes?.count || 0) > 0 && (
            <div data-testid="ops-audit-orphan-washes">
              <div className="text-[11px] uppercase tracking-wider font-bold text-amber-700 mb-2">
                Wash certificates without a preceding IN ({data.orphan_washes.count})
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-1 px-2">Wash no</th>
                      <th className="py-1 px-2">Cert</th>
                      <th className="py-1 px-2">Tank</th>
                      <th className="py-1 px-2">Customer</th>
                      <th className="py-1 px-2">Cleaning date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.orphan_washes.items.slice(0, 20).map((w) => (
                      <tr key={w.id} className="border-b border-gray-100">
                        <td className="py-1 px-2 font-mono">{w.wash_no}</td>
                        <td className="py-1 px-2 font-mono text-gray-600">{w.cleaning_cert_no || "—"}</td>
                        <td className="py-1 px-2 font-mono font-bold">{w.tank_number}</td>
                        <td className="py-1 px-2 text-gray-600">{(w.customer || "—").slice(0, 28)}</td>
                        <td className="py-1 px-2 font-mono">{(w.cleaning_date || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.orphan_washes.items.length > 20 && (
                  <div className="text-[11px] text-gray-500 mt-2">… and {data.orphan_washes.items.length - 20} more.</div>
                )}
              </div>
            </div>
          )}
          {total === 0 && (
            <div className="text-emerald-700 text-xs">
              No issues to triage. Gate ops compliance is on point for this window.
            </div>
          )}
          <div className="text-[10px] text-gray-400">
            Generated {data.generated_at ? new Date(data.generated_at).toLocaleString("en-GB") : "—"}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user, depot: pickedDepot, depots } = useAuth();
  // Operators see operational metrics only — no revenue, no wash $ amounts.
  const showFinancials = user?.role !== "operator";

  // Reference month — pre-loads the *previous* calendar month so the dashboard
  // opens on the most recent fully-closed period (the one operators actually
  // want to see for billing reconciliation). The arrows step backward/forward.
  const previousMonthIso = () => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  const [refMonth, setRefMonth] = useState(previousMonthIso);

  const stepMonth = (deltaMonths) => {
    const [y, m] = refMonth.split("-").map(Number);
    const d = new Date(y, m - 1 + deltaMonths, 1);
    setRefMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const goCurrentMonth = () => {
    const d = new Date();
    setRefMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const monthLabel = (() => {
    const [y, m] = refMonth.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleString("en-GB", { month: "long", year: "numeric" });
  })();

  const [kpis, setKpis] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [depot, setDepot] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [washTrends, setWashTrends] = useState(null);
  const [repairTrends, setRepairTrends] = useState(null);

  useEffect(() => {
    const calls = [
      api.get(`/dashboard/kpis?yyyy_mm=${refMonth}`),
      api.get("/dashboard/depot-composition"),
      api.get("/dashboard/daily-throughput?days=90"),
      api.get("/dashboard/wash-trends?months=6"),
      api.get("/dashboard/repair-trends?months=6"),
    ];
    if (showFinancials) calls.splice(1, 0, api.get("/dashboard/revenue-by-carrier?months=6"));
    Promise.all(calls).then((results) => {
      setKpis(results[0].data);
      if (showFinancials) {
        setRevenue(results[1].data);
        setDepot(results[2].data); setHeatmap(results[3].data);
        setWashTrends(results[4].data); setRepairTrends(results[5].data);
      } else {
        setDepot(results[1].data); setHeatmap(results[2].data);
        setWashTrends(results[3].data); setRepairTrends(results[4].data);
      }
    }).catch(console.error);
  }, [showFinancials, refMonth, pickedDepot]);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Operations Dashboard"
        subtitle="Live snapshot of depot throughput, stock composition, billing and wash activity."
        testid="dashboard-header"
      />

      <div className="p-6 sm:p-8 space-y-6">
        {/* ───── Month selector ───── */}
        <div className="flex flex-wrap items-center justify-between gap-3 border border-gray-200 bg-white px-4 py-3" data-testid="dashboard-month-picker">
          <div className="flex items-center gap-3">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500">Reference month</div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => stepMonth(-1)}
                className="ps-btn ps-btn-secondary h-8 w-8 p-0 justify-center"
                title="Previous month"
                data-testid="dashboard-month-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <input
                type="month"
                value={refMonth}
                onChange={(e) => e.target.value && setRefMonth(e.target.value)}
                className="ps-input h-8 font-mono text-sm w-36"
                data-testid="dashboard-month-input"
              />
              <button
                onClick={() => stepMonth(1)}
                className="ps-btn ps-btn-secondary h-8 w-8 p-0 justify-center"
                title="Next month"
                data-testid="dashboard-month-next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="hidden sm:block font-bold text-lg">{monthLabel}</div>
          </div>
          <button
            onClick={goCurrentMonth}
            className="ps-btn ps-btn-secondary"
            title="Jump to the current calendar month"
            data-testid="dashboard-month-current"
          >
            <Calendar className="w-4 h-4" /> This month
          </button>
        </div>

        {/* Operations audit card removed — noise from historical gaps
            (old OUTs without IN, orphan washes) that operators can't
            action. Data-quality scheduler still logs these nightly to
            audit_logs for admins if needed. */}

        {/* ───── Per-depot breakdown (only when "All my depots" is picked and
             the user has more than one accessible depot) ───── */}
        {showFinancials && (!pickedDepot || pickedDepot === "ALL") && depots?.my_depots?.length > 1 && (
          <ByDepotPanel yyyyMm={refMonth} monthLabel={monthLabel} />
        )}

        {/* ───── ZONE 1: KPI strip ───── */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${showFinancials ? "lg:grid-cols-5" : "lg:grid-cols-3"} gap-4`}>
          <KpiCard
            label="Containers in depot"
            value={fmtNum(kpis?.in_depot)}
            sub={depot ? `${depot.by_carrier?.[0]?.label || "—"} leads with ${fmtNum(depot.by_carrier?.[0]?.value || 0)}` : ""}
            icon={Container}
            accent="bg-blue-50 text-[#006E9E]"
            testid="kpi-in-depot"
          />
          {showFinancials && (
            <KpiCard
              label={`Revenue · ${monthLabel}`}
              value={fmtMoney(kpis?.revenue_this_month)}
              sub={
                kpis?.revenue_breakdown_current?.eur_native > 0
                  ? `incl. €${kpis.revenue_breakdown_current.eur_native.toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} @ ${kpis.revenue_breakdown_current.fx_used?.toFixed(2) ?? "?"}  ·  vs ${fmtMoney(kpis?.revenue_last_month)} prev`
                  : `vs ${fmtMoney(kpis?.revenue_last_month)} prev month`
              }
              icon={DollarSign}
              accent="bg-emerald-50 text-emerald-700"
              delta={kpis?.revenue_delta_pct}
              testid="kpi-revenue"
            />
          )}
          <KpiCard
            label={`Lifts · ${monthLabel}`}
            value={fmtNum(kpis?.lifts_this_month)}
            sub={`vs ${fmtNum(kpis?.lifts_last_month)} prev month`}
            icon={ArrowRightLeft}
            accent="bg-amber-50 text-amber-700"
            delta={kpis?.lifts_delta_pct}
            testid="kpi-lifts"
          />
          {showFinancials ? (
            <KpiCard
              label={`Wash revenue · ${monthLabel}`}
              value={fmtMoney(kpis?.wash_total_revenue ?? ((kpis?.wash_food_revenue ?? 0) + (kpis?.wash_chem_revenue ?? 0) + (kpis?.wash_other_revenue ?? 0) + (kpis?.wash_water_revenue ?? 0)))}
              sub={`${kpis?.wash_food_count ?? 0} food · ${kpis?.wash_chem_count ?? 0} chem${kpis?.wash_other_count ? ` · ${kpis.wash_other_count} other` : ""}${kpis?.wash_water_count ? ` · ${kpis.wash_water_count} water` : ""}`}
              icon={Droplets}
              accent="bg-sky-50 text-[#008DC6]"
              testid="kpi-wash"
            />
          ) : (
            <KpiCard
              label={`Washes · ${monthLabel}`}
              value={fmtNum(kpis?.wash_total_count ?? ((kpis?.wash_food_count ?? 0) + (kpis?.wash_chem_count ?? 0) + (kpis?.wash_other_count ?? 0) + (kpis?.wash_water_count ?? 0)))}
              sub={`${kpis?.wash_food_count ?? 0} food · ${kpis?.wash_chem_count ?? 0} chem${kpis?.wash_other_count ? ` · ${kpis.wash_other_count} other` : ""}${kpis?.wash_water_count ? ` · ${kpis.wash_water_count} water` : ""}`}
              icon={Droplets}
              accent="bg-sky-50 text-[#008DC6]"
              testid="kpi-wash-count"
            />
          )}
          {showFinancials ? (
            <KpiCard
              label={`Repair revenue · ${monthLabel}`}
              value={fmtMoney(kpis?.repairs?.invoiced_value ?? 0)}
              sub={`${kpis?.repairs?.invoiced_count ?? 0} invoiced · ${kpis?.repairs?.open_count ?? 0} open (${fmtMoney(kpis?.repairs?.open_value ?? 0)})`}
              icon={Wrench}
              accent="bg-orange-50 text-orange-700"
              testid="kpi-repairs"
            />
          ) : (
            <KpiCard
              label={`Repairs · ${monthLabel}`}
              value={fmtNum(kpis?.repairs?.invoiced_count ?? 0)}
              sub={`${kpis?.repairs?.open_count ?? 0} open in pipeline`}
              icon={Wrench}
              accent="bg-orange-50 text-orange-700"
              testid="kpi-repairs-count"
            />
          )}
        </div>

        {/* ───── ZONE 2: Revenue by carrier — hidden for operators ───── */}
        {showFinancials && (
        <SectionCard
          title="Monthly revenue by carrier"
          subtitle={
            revenue?.grand_total !== undefined
              ? `Stacked totals from generated invoices · last 6 months · ${fmtMoney(revenue.grand_total)} total`
              : "Stacked totals from generated invoices · last 6 months"
          }
          icon={BarChart3}
          testid="zone-revenue"
        >
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={revenue?.rows || []} margin={{ top: 24, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D2" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "monospace" }} stroke="#94A3B8" />
              <YAxis tick={{ fontSize: 11, fontFamily: "monospace" }} stroke="#94A3B8"
                     tickFormatter={fmtShortMoney} />
              <Tooltip content={<ChartTooltip valueFormatter={(v) => fmtMoney(v)} hideZeros />} />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
              {(revenue?.carriers || []).map((cc, i) => {
                const isTop = i === (revenue?.carriers?.length || 1) - 1;
                return (
                  <Bar
                    key={cc}
                    dataKey={cc}
                    stackId="rev"
                    fill={CARRIER_PALETTE[i % CARRIER_PALETTE.length]}
                    radius={isTop ? [4, 4, 0, 0] : 0}
                  >
                    {isTop && (
                      <LabelList
                        dataKey="total"
                        position="top"
                        formatter={(v) => (v > 0 ? fmtShortMoney(v) : "")}
                        style={{ fontSize: 11, fontFamily: "monospace", fontWeight: 700, fill: "#0F172A" }}
                      />
                    )}
                  </Bar>
                );
              })}
            </BarChart>
          </ResponsiveContainer>
          {revenue?.rows?.length > 0 && (
            <div
              className="grid gap-2 mt-3 pt-3 border-t border-gray-200"
              style={{ gridTemplateColumns: `repeat(${revenue.rows.length}, minmax(0, 1fr))` }}
              data-testid="revenue-monthly-totals"
            >
              {revenue.rows.map((r) => (
                <div key={r.month} className="text-center">
                  <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 font-mono">
                    {r.month}
                  </div>
                  <div className="font-mono text-lg font-black text-gray-900">
                    {fmtMoney(r.total)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
        )}

        {/* ───── ZONE 3 + 4 row: Depot composition + ISO breakdown ───── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <SectionCard
            title="Depot composition — by carrier"
            subtitle={depot ? `${fmtNum(depot.total)} containers in depot` : ""}
            icon={PieIcon}
            testid="zone-depot-carrier"
          >
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={(depot?.by_carrier || []).slice(0, 8)}
                  dataKey="value"
                  nameKey="label"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                >
                  {(depot?.by_carrier || []).slice(0, 8).map((_, i) => (
                    <Cell key={i} fill={CARRIER_PALETTE[i % CARRIER_PALETTE.length]} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const p = payload[0];
                    return (
                      <div className="bg-white border border-gray-200 shadow-lg p-3 text-xs font-mono">
                        <div className="font-bold text-gray-900">{p.payload.label}</div>
                        <div className="text-gray-600">{fmtNum(p.value)} containers ({((p.value / depot.total) * 100).toFixed(1)}%)</div>
                      </div>
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {(depot?.by_carrier || []).slice(0, 8).map((c, i) => (
                <div key={c.label} className="flex items-center gap-2 text-xs">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: CARRIER_PALETTE[i % CARRIER_PALETTE.length] }} />
                  <span className="font-mono text-gray-700 truncate">{c.label}</span>
                  <span className="font-mono font-bold ml-auto">{fmtNum(c.value)}</span>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title="By ISO type"
            subtitle="Container & tank classes"
            icon={Container}
            testid="zone-depot-iso"
          >
            <ResponsiveContainer width="100%" height={280}>
              <BarChart layout="vertical" data={depot?.by_iso || []} margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D2" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fontFamily: "monospace" }} stroke="#94A3B8" />
                <YAxis dataKey="label" type="category" tick={{ fontSize: 11, fontFamily: "monospace" }} width={60} stroke="#94A3B8" />
                <Tooltip content={<ChartTooltip valueFormatter={fmtNum} />} />
                <Bar dataKey="value" fill="#006E9E" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </SectionCard>

          {showFinancials && (
          <SectionCard
            title="Wash split (MTD)"
            subtitle="Food · Chemical · Water · Unclassified revenue"
            icon={Droplets}
            testid="zone-wash-split"
          >
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={[
                    { label: "Food", value: kpis?.wash_food_revenue || 0 },
                    { label: "Chemical", value: kpis?.wash_chem_revenue || 0 },
                    // Water Solutions — the lightweight STC rinse flow. Billed
                    // flat-fee from the water_solutions collection, folded in
                    // here so the "wash basket" matches what finance sees on
                    // the carrier invoices (STOLT especially).
                    { label: "Water", value: kpis?.wash_water_revenue || 0 },
                    // Unclassified washes (STANDARD / UNKNOWN / null category).
                    // Surfaced explicitly so operators can see that some June
                    // washes need reclassification instead of the pie looking
                    // like 100 % food when chem washes are just miscoded.
                    { label: "Unclassified", value: kpis?.wash_other_revenue || 0 },
                  ].filter((s) => s.value > 0)}
                  dataKey="value"
                  nameKey="label"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={3}
                >
                  <Cell fill="#99D420" />
                  <Cell fill="#F1471D" />
                  <Cell fill="#38BDF8" />
                  <Cell fill="#94A3B8" />
                </Pie>
                <Tooltip content={<ChartTooltip valueFormatter={(v) => fmtMoney(v)} />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-4 gap-2 mt-2">
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#99D420]">Food</div>
                <div className="font-mono text-2xl font-black text-gray-900">{fmtMoney(kpis?.wash_food_revenue)}</div>
                <div className="text-[10px] text-gray-500">{kpis?.wash_food_count ?? 0} washes</div>
              </div>
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#F1471D]">Chemical</div>
                <div className="font-mono text-2xl font-black text-gray-900">{fmtMoney(kpis?.wash_chem_revenue)}</div>
                <div className="text-[10px] text-gray-500">{kpis?.wash_chem_count ?? 0} washes</div>
              </div>
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#38BDF8]">Water</div>
                <div className="font-mono text-2xl font-black text-gray-900" data-testid="kpi-wash-water-revenue">{fmtMoney(kpis?.wash_water_revenue)}</div>
                <div className="text-[10px] text-gray-500">{kpis?.wash_water_count ?? 0} jobs</div>
              </div>
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500">Unclassified</div>
                <div className="font-mono text-2xl font-black text-gray-900">{fmtMoney(kpis?.wash_other_revenue)}</div>
                <div className="text-[10px] text-gray-500">{kpis?.wash_other_count ?? 0} washes</div>
              </div>
            </div>
          </SectionCard>
          )}
        </div>

        {/* ───── ZONE 4: Daily throughput heatmap ───── */}
        <SectionCard
          title="Daily gate throughput"
          subtitle="Last 90 days · IN + OUT activity intensity"
          icon={Activity}
          testid="zone-heatmap"
        >
          <DailyHeatmap rows={heatmap?.rows || []} />
        </SectionCard>

        {/* ───── ZONE 5: Wash trends — counts always, revenue only for finance views ───── */}
        <SectionCard
          title="Wash activity trend"
          subtitle={showFinancials
            ? "Monthly count & revenue — Food (green) · Chemical (orange) · Water (blue)"
            : "Monthly count — Food (green) · Chemical (orange) · Water (blue)"}
          icon={Droplets}
          testid="zone-wash-trend"
        >
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={washTrends?.rows || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D2" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "monospace" }} stroke="#94A3B8" />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fontFamily: "monospace" }} stroke="#94A3B8" />
              {showFinancials && (
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fontFamily: "monospace" }} stroke="#94A3B8" tickFormatter={fmtShortMoney} />
              )}
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
              <Bar yAxisId="left" dataKey="FOOD" fill="#99D420" stackId="washes" radius={[0, 0, 0, 0]} />
              <Bar yAxisId="left" dataKey="CHEMICAL" fill="#F1471D" stackId="washes" radius={[0, 0, 0, 0]} />
              {/* Water Solutions — flat-fee STC rinses stacked on top of
                  food/chem so the bar matches the real monthly wash basket. */}
              <Bar yAxisId="left" dataKey="WATER" fill="#38BDF8" stackId="washes" radius={[4, 4, 0, 0]} />
              {showFinancials && (
                <>
                  <Line yAxisId="right" type="monotone" dataKey="food_rev" stroke="#15803D" strokeWidth={2}
                        dot={{ r: 4, fill: "#15803D" }} name="Food revenue" />
                  <Line yAxisId="right" type="monotone" dataKey="chem_rev" stroke="#A16207" strokeWidth={2}
                        dot={{ r: 4, fill: "#A16207" }} name="Chem revenue" />
                  <Line yAxisId="right" type="monotone" dataKey="water_rev" stroke="#0369A1" strokeWidth={2}
                        dot={{ r: 4, fill: "#0369A1" }} name="Water revenue" />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </SectionCard>

        {/* ───── ZONE 6: Tank repairs pipeline + trend ───── */}
        <RepairsCard repairs={kpis?.repairs} showFinancials={showFinancials} monthLabel={monthLabel} />
        {repairTrends && (repairTrends.rows || []).some((r) => (r.raised_n || 0) > 0 || (r.invoiced_n || 0) > 0) && (
          <SectionCard
            title="Repair activity trend"
            subtitle={
              showFinancials
                ? "Monthly count of estimates raised (bars) vs invoiced revenue (line)"
                : "Monthly count of estimates raised vs invoiced"
            }
            icon={Wrench}
            testid="zone-repair-trend"
          >
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={repairTrends?.rows || []} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D2" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "monospace" }} stroke="#94A3B8" />
                <YAxis yAxisId="left" tick={{ fontSize: 11, fontFamily: "monospace" }} stroke="#94A3B8" allowDecimals={false} />
                {showFinancials && (
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fontFamily: "monospace" }}
                         stroke="#94A3B8" tickFormatter={fmtShortMoney} />
                )}
                <Tooltip content={<ChartTooltip valueFormatter={(v, name) => (name === "invoiced_rev" ? fmtMoney(v) : fmtNum(v))} hideZeros />} />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
                <Bar yAxisId="left" dataKey="raised_n" name="Raised" fill="#F97316" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="left" dataKey="invoiced_n" name="Invoiced" fill="#0F172A" radius={[4, 4, 0, 0]} />
                {showFinancials && (
                  <Line yAxisId="right" type="monotone" dataKey="invoiced_rev" name="Invoiced revenue"
                        stroke="#B45309" strokeWidth={2} dot={{ r: 4 }} />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </SectionCard>
        )}
      </div>
    </div>
  );
}

// ---------- Sub: Tank Repairs pipeline ----------
function RepairsCard({ repairs, showFinancials, monthLabel }) {
  const buckets = [
    { key: "quoting",     label: "Quoting",     hint: "DRAFT + SENT", accent: "bg-gray-100 text-gray-700 border-gray-300" },
    { key: "approved",    label: "Approved",    hint: "Ready to start", accent: "bg-blue-50 text-[#006E9E] border-blue-200" },
    { key: "in_progress", label: "In progress", hint: "Mechanic working", accent: "bg-amber-50 text-amber-800 border-amber-300" },
    { key: "ready",       label: "Ready to invoice", hint: "COMPLETED", accent: "bg-emerald-50 text-emerald-800 border-emerald-300" },
    { key: "invoiced",    label: `Invoiced · ${monthLabel}`, hint: "This month", accent: "bg-[#F5F0DA] text-gray-800 border-[#E5E0D2]" },
  ];
  const openCount = repairs?.open_count ?? 0;
  const openValue = repairs?.open_value ?? 0;
  return (
    <SectionCard
      title="Tank repairs pipeline"
      subtitle={
        showFinancials
          ? `${fmtNum(openCount)} open estimate(s) · ${fmtMoney(openValue)} in the pipeline`
          : `${fmtNum(openCount)} open estimate(s)`
      }
      icon={Wrench}
      action={
        <a
          href="/tank-repairs"
          className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#006E9E] hover:underline"
          data-testid="dashboard-repairs-open"
        >
          Open module →
        </a>
      }
      testid="zone-repairs"
    >
      {!repairs ? (
        <div className="text-center text-gray-400 py-6 text-sm">Loading…</div>
      ) : openCount === 0 && (repairs.invoiced_count || 0) === 0 ? (
        <div className="text-center text-gray-400 py-6 text-sm" data-testid="repairs-empty">
          No repair estimates in the system yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {buckets.map((b) => {
            const n = repairs[`${b.key}_count`] ?? 0;
            const v = repairs[`${b.key}_value`];
            return (
              <div
                key={b.key}
                className={`border ${b.accent} px-3 py-3 rounded-sm`}
                data-testid={`repairs-bucket-${b.key}`}
              >
                <div className="text-[10px] uppercase tracking-[0.16em] font-bold opacity-80">{b.label}</div>
                <div className="font-mono text-3xl font-black mt-1">{fmtNum(n)}</div>
                {showFinancials && v !== undefined && (
                  <div className="text-[11px] font-mono opacity-70 mt-0.5">{fmtMoney(v)}</div>
                )}
                <div className="text-[10px] opacity-60 mt-1">{b.hint}</div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Per-depot breakdown — shown on the admin's dashboard when "All my depots"
// is picked. Three compact cards side-by-side so you can see which country
// is pulling its weight at a glance.
const DEPOT_FLAG = { "GH-TEM": "🇬🇭", "TG-LFW": "🇹🇬", "GA-LBV": "🇬🇦" };

function ByDepotPanel({ yyyyMm, monthLabel }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/dashboard/by-depot?yyyy_mm=${yyyyMm}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiError(e)));
  }, [yyyyMm]);

  if (err) return null;
  const rows = data?.rows || [];
  if (!rows.length) return null;

  // Totals row so the strip tells a complete story on its own.
  const totals = rows.reduce((acc, r) => ({
    in_depot: acc.in_depot + (r.in_depot || 0),
    lifts: acc.lifts + (r.lifts_this_month || 0),
    revenue: acc.revenue + (r.revenue_this_month || 0),
    wash: acc.wash + (r.wash_count_this_month || 0),
  }), { in_depot: 0, lifts: 0, revenue: 0, wash: 0 });

  return (
    <SectionCard
      icon={Container}
      title="Per-depot breakdown"
      subtitle={`All depots · ${monthLabel}`}
      testid="dashboard-by-depot"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {rows.map((r) => (
          <div
            key={r.depot_code}
            className="rounded-md border border-gray-200 bg-white p-4 flex flex-col gap-2"
            data-testid={`by-depot-${r.depot_code}`}
          >
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold tracking-[0.14em] font-mono text-gray-700">
                <span className="text-base leading-none mr-1">{DEPOT_FLAG[r.depot_code] || "📍"}</span>
                {r.depot_code}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
              <div className="text-gray-500">In depot</div>
              <div className="text-right font-mono font-bold">{fmtNum(r.in_depot)}</div>
              <div className="text-gray-500">Lifts</div>
              <div className="text-right font-mono font-bold">{fmtNum(r.lifts_this_month)}</div>
              <div className="text-gray-500">Washes</div>
              <div className="text-right font-mono font-bold">{fmtNum(r.wash_count_this_month)}</div>
              <div className="text-gray-500">Revenue</div>
              <div className="text-right font-mono font-bold text-[#1A1410]">{fmtMoney(r.revenue_this_month)}</div>
            </div>
          </div>
        ))}
        {/* Totals card — only shows when there's more than one depot with data */}
        <div
          className="rounded-md border-2 border-[#99D420]/60 bg-[#99D420]/5 p-4 flex flex-col gap-2"
          data-testid="by-depot-total"
        >
          <div className="text-xs font-bold tracking-[0.14em] font-mono text-[#1A1410]">
            🌐 TOTAL
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
            <div className="text-gray-500">In depot</div>
            <div className="text-right font-mono font-bold">{fmtNum(totals.in_depot)}</div>
            <div className="text-gray-500">Lifts</div>
            <div className="text-right font-mono font-bold">{fmtNum(totals.lifts)}</div>
            <div className="text-gray-500">Washes</div>
            <div className="text-right font-mono font-bold">{fmtNum(totals.wash)}</div>
            <div className="text-gray-500">Revenue</div>
            <div className="text-right font-mono font-bold text-[#1A1410]">{fmtMoney(totals.revenue)}</div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}
